import { useState, useEffect, useRef } from 'react';
import './operations.css';

/* CAPAs, nonconformances and the SOP index were illustrative arrays here. Quality now reads
   controlled documents from the database, so they were removed rather than left dangling.
   quality.capas, quality.nonconformances and quality.audits are all modelled and populated
   in the schema; surfacing them is outstanding work, not a gap in the data model. */
import {
  fetchChannels, fetchMessages, fetchColleagues, startDirect, postMessage, markRead,
  type Channel, type Message, type Colleague,
} from '../lib/chorus';
import {
  fetchDocuments, fetchRegister, createDocument, submitForApproval, approveDocument,
  acknowledgeDocument, DOC_TYPES,
  fetchNonconformances, fetchCapas, raiseNonconformance, raiseCapa, updateCapa,
  updateNonconformance, addCapaAction, updateCapaAction, verifyEffectiveness,
  NC_SEVERITY, NC_SOURCE, NC_STATUS, CAPA_STATUS, CAPA_TYPES,
  type ControlledDocument, type RegisterRow, type Nonconformance, type Capa,
} from '../lib/quality';
import {
  crewGates, listCrew, listRoles, listCompetences, roleRequirements, accessHistory,
  openRecord, addCrew, addRole, addCompetence, requireCompetence, unrequireCompetence,
  assignRole, updateRecord, setReportingLine, setExpenseApprover, listMembers, type Member,
  type CrewMember, type CrewRole, type Competence, type RoleRequirement,
  type EmployeeRecord, type AccessEntry,
} from '../lib/crew';
import {
  fetchOrgChart, fetchUnits, addUnit, addPosition, updatePosition, assignHolder,
  UNIT_TYPES, SENIORITY, ENGAGEMENT,
  type OrgPosition, type OrgUnit,
} from '../lib/org';
import { useDialog } from '../lib/ui';
import { GoalsPanel } from '../components/GoalsPanel';
import { AccessPanel } from '../components/AccessPanel';
import { Attachments } from '../components/Attachments';
import { NewContract } from '../components/CreateForms';
import { fetchContractPositions, fetchMissingExecutedCopies, updateContract,
         CONTRACT_STATUS, type ContractPosition } from '../lib/commercial';
import {
  fetchApplications, fetchCalls, fetchFunders, createApplication, createCall, createFunder,
  updateApplication, deleteApplication, daysUntil, fetchContractsForLinking,
  APP_STATUSES, CLOSED, FUNDER_SEARCHES,
  type Application, type Call, type Funder, type AppStatus,
} from '../lib/grants';
import { onFail } from '../lib/errors';
import { TENANT } from '@tenant';

/* =====================================================================================
   Contracts, Quality, Crew, Chorus and Grants.

   Grouped in one file because each is a focused surface over a schema that already
   carries the logic. The interesting behaviour in all five lives in the database:
   computed notice deadlines, CAPA effectiveness checks, role-mapped competence gaps,
   object-anchored threads and claim windows.
   ===================================================================================== */

/* Live, not fixed, or every countdown below drifts by a day for every day that passes. */
const TODAY = new Date();
const daysTo = (d: string) => Math.round((new Date(d).getTime() - TODAY.getTime()) / 86_400_000);
const money = (n: number, c = 'GBP') =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n);

/* ===================================================================== CONTRACTS === */


/* Contracts come from the tenant seed only. They used to live in this file and were
   therefore present in every build; see data/scira.examples.ts. */
const TENANT_CONTRACTS = TENANT.seed.contract as any[];
const CONTRACTS = TENANT_CONTRACTS;

export function ContractsView() {
  const [creating, setCreating] = useState(false);
  const [positions, setPositions] = useState<ContractPosition[]>([]);
  const [unevidenced, setUnevidenced] = useState<any[]>([]);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);

  useEffect(() => {
    fetchContractPositions().then(setPositions).catch(onFail('Contract positions', () => setPositions([])));
    fetchMissingExecutedCopies().then(setUnevidenced).catch(onFail('Contract positions', () => setUnevidenced([])));
  }, [nonce]);

  const notices = CONTRACTS
    .filter((c) => c.autoRenew && c.expires && c.noticeDays)
    .map((c) => ({ ...c, noticeDeadline: new Date(new Date(c.expires!).getTime() - c.noticeDays! * 86_400_000).toISOString().slice(0, 10) }));

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Contracts</h1>
        <p className="page-sub">
          The point is not storing the PDF. It is knowing what you promised, when it is due, and
          whether an auto-renewal is about to commit you to another year.
        </p>
      </div>

      <div className="g-call-actions">
        <button className="btn-compose q-new" onClick={() => setCreating(true)}>Add contract</button>
      </div>

      {/* A contract is not finished until a fully executed copy is on file. This chases the
          ones that claim to be executed with nothing signed attached, because a signature
          nobody can produce is not a signature. */}
      {unevidenced.length > 0 && (
        <div className="q-owed card">
          <strong>{unevidenced.length} contract{unevidenced.length > 1 ? 's' : ''} with no fully executed copy on file.</strong>
          <span>{unevidenced.map((c: any) => c.reference).join(', ')}</span>
        </div>
      )}

      {positions.length > 0 && (
        <section className="site-block">
          <h2>Position</h2>
          <table className="data">
            <thead>
              <tr><th>Reference</th><th>Counterparty</th><th>Status</th>
                  <th className="num">Value</th><th className="num">Invoiced</th>
                  <th className="num">Received</th><th>Notice deadline</th><th className="num">Open obligations</th></tr>
            </thead>
            <tbody>
              {positions.map((c) => (
                <tr key={c.id}>
                  <td><span className="mono">{c.reference}</span></td>
                  <td>{c.counterparty_name}</td>
                  <td>
                    <select value={c.status}
                            onChange={(e) => updateContract(c.id, { status: e.target.value }).then(reload)}>
                      {CONTRACT_STATUS.map((x) => <option key={x} value={x}>{x.replace(/_/g, ' ')}</option>)}
                    </select>
                  </td>
                  <td className="num">{c.contract_value != null ? `${c.currency} ${Number(c.contract_value).toLocaleString('en-GB')}` : '—'}</td>
                  <td className="num">{Number(c.invoiced).toLocaleString('en-GB')}
                    {c.pct_invoiced != null && <span className="muted"> ({c.pct_invoiced}%)</span>}</td>
                  <td className="num">{Number(c.received).toLocaleString('en-GB')}</td>
                  <td>{c.notice_deadline
                    ? <span className={`pill ${new Date(c.notice_deadline) < new Date(Date.now() + 90 * 86400000) ? 'risk' : 'mute'}`}>
                        {new Date(c.notice_deadline).toLocaleDateString('en-GB')}</span>
                    : <span className="muted">—</span>}</td>
                  <td className="num">{c.open_obligations > 0
                    ? <span className="pill warn">{c.open_obligations}</span> : '0'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {notices.map((c) => (
        <div key={c.ref} className="insight card">
          <strong>{c.title} auto-renews on {c.expires}.</strong>
          <span>
            Notice must be served by <strong>{c.noticeDeadline}</strong>, which is {daysTo(c.noticeDeadline)} days
            away. A short notice window is how companies get trapped into another term.
          </span>
        </div>
      ))}

      <div className="card table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Contract</th><th>Counterparty</th><th>Type</th><th>Status</th>
              <th className="num">Value</th><th className="num">Expires</th><th>Key clauses</th><th>Risk</th>
            </tr>
          </thead>
          <tbody>
            {CONTRACTS.map((c) => (
              <tr key={c.ref}>
                <td>
                  <div className="cell-title">{c.title}</div>
                  <div className="cell-sub mono">{c.ref}</div>
                </td>
                <td>{c.party}</td>
                <td>{c.type.replace(/_/g, ' ')}</td>
                <td>
                  <span className={`pill ${c.status === 'active' ? 'ok' : c.status === 'draft' ? 'warn' : 'mute'}`}>
                    {c.status}
                  </span>
                </td>
                <td className="num">{c.value ? money(c.value, c.currency) : <span className="muted">—</span>}</td>
                <td className="num">{c.expires ?? <span className="muted">perpetual</span>}</td>
                <td>{c.flags.map((f: string) => <span key={f} className="pill mute" style={{ marginRight: 3 }}>{f.replace(/_/g, ' ')}</span>)}</td>
                <td>
                  <span className={`pill ${c.risk === 'critical' ? 'risk' : c.risk === 'high' ? 'warn' : 'mute'}`}>{c.risk}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="table-note">
          Clause flags are stored as queryable booleans rather than buried in text, so a diligence
          question like "which contracts contain a change of control provision" is one query, not a
          weekend of reading.
        </div>
      </div>

      <div className="note-stack">
        {CONTRACTS.filter((c) => c.note).map((c) => (
          <div key={c.ref} className={`card note-card ${c.risk === 'critical' ? 'crit' : ''}`}>
            <div className="label mono">{c.ref}</div>
            <p>{c.note}</p>
          </div>
        ))}
      </div>

      {creating && (
        <NewContract onClose={() => setCreating(false)}
                     onDone={() => { setCreating(false); reload(); }} />
      )}
    </div>
  );
}

/* ======================================================================= QUALITY === */



export function QualityView() {
  const [docs, setDocs] = useState<ControlledDocument[]>([]);
  const [register, setRegister] = useState<RegisterRow[]>([]);
  const [people, setPeople] = useState<Colleague[]>([]);
  const [me, setMe] = useState<string | null>(null);
  const [tab, setTab] = useState<'documents' | 'register' | 'capa'>('documents');
  const [ncs, setNcs] = useState<Nonconformance[]>([]);
  const [capas, setCapas] = useState<Capa[]>([]);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);

  useEffect(() => {
    fetchDocuments().then(setDocs).catch((e) => setErr(String(e.message ?? e)));
    fetchRegister().then(setRegister).catch(onFail('The document register', () => setRegister([])));
    fetchColleagues().then(setPeople).catch(onFail('The document register', () => setPeople([])));
    fetchNonconformances().then(setNcs).catch(onFail('Colleagues', () => setNcs([])));
    fetchCapas().then(setCapas).catch(onFail('Colleagues', () => setCapas([])));
    import('../lib/supabase').then((m) => m.currentMember()).then((m) => setMe(m?.id ?? null)).catch(() => {});
  }, [nonce]);

  const mine = register.filter((r) => r.member_id === me && r.outstanding);

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Quality</h1>
        <p className="page-sub">
          Controlled documents under ISO 13485. The author of a procedure cannot authorise it
          and only the named approver can sign: both are enforced in the database, because an
          approval anyone could apply is not an approval.
        </p>
      </div>

      {err && <p className="block-error" role="alert">{err}</p>}

      {mine.length > 0 && (
        <div className="q-owed card">
          <strong>{mine.length} document{mine.length > 1 ? 's' : ''} awaiting your signature.</strong>
          <span>Open the register to read and acknowledge.</span>
          <button className="btn-compose" onClick={() => setTab('register')}>Go to register</button>
        </div>
      )}

      <div className="q-tabs">
        <button className={tab === 'documents' ? 'on' : ''} onClick={() => setTab('documents')}>
          Documents ({docs.length})
        </button>
        <button className={tab === 'register' ? 'on' : ''} onClick={() => setTab('register')}>
          Training register ({register.filter((r) => r.outstanding).length} outstanding)
        </button>
        <button className={tab === 'capa' ? 'on' : ''} onClick={() => setTab('capa')}>
          Nonconformances &amp; CAPA ({ncs.filter((n) => n.status !== 'closed' && n.status !== 'not_a_nc').length} open)
        </button>
        <button className="btn-compose q-new" onClick={() => setCreating(!creating)}>
          {creating ? 'Cancel' : 'New document'}
        </button>
      </div>

      {creating && <NewDocument onDone={() => { setCreating(false); reload(); }} />}

      {tab === 'documents' && (
        <>
          {docs.length === 0 && !creating && (
            <p className="block-empty">
              No controlled documents yet. Create one and it starts as a draft that only you
              can edit until you submit it for authorisation.
            </p>
          )}
          {docs.map((d) => (
            <DocumentCard key={d.id} doc={d} people={people} me={me} onChanged={reload} />
          ))}
        </>
      )}

      {tab === 'register' && <Register rows={register} me={me} onChanged={reload} />}

      {tab === 'capa' && <CapaTab ncs={ncs} capas={capas} docs={docs} onChanged={reload} />}
    </div>
  );
}

function NewDocument({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({
    docNumber: '', title: '', docType: 'sop', processArea: '',
    trainingRequired: false, reviewMonths: 24, changeSummary: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setErr(null); setBusy(true);
    try { await createDocument(f); onDone(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="composer card q-form">
      <div className="composer-row">
        <input placeholder="SOP-013" value={f.docNumber} disabled={busy}
               onChange={(e) => setF({ ...f, docNumber: e.target.value })} style={{ maxWidth: '9rem' }} />
        <input placeholder="Title" value={f.title} disabled={busy} className="grow"
               onChange={(e) => setF({ ...f, title: e.target.value })} />
      </div>
      <div className="composer-row">
        <select value={f.docType} disabled={busy}
                onChange={(e) => setF({ ...f, docType: e.target.value })}>
          {DOC_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <input placeholder="Process area" value={f.processArea} disabled={busy}
               onChange={(e) => setF({ ...f, processArea: e.target.value })} />
        <label className="composer-ack">
          Review every
          <input type="number" min={1} max={60} value={f.reviewMonths} disabled={busy}
                 style={{ width: '4rem' }}
                 onChange={(e) => setF({ ...f, reviewMonths: Number(e.target.value) })} />
          months
        </label>
        {/* Marking this sends a read-and-sign request to everyone the moment it is approved.
            Silence is not evidence that a policy was read, and an auditor asks for the register. */}
        <label className="composer-ack">
          <input type="checkbox" checked={f.trainingRequired} disabled={busy}
                 onChange={(e) => setF({ ...f, trainingRequired: e.target.checked })} />
          Everyone must read and sign
        </label>
      </div>
      <textarea rows={2} placeholder="What changed, and why" value={f.changeSummary} disabled={busy}
                onChange={(e) => setF({ ...f, changeSummary: e.target.value })} />
      {err && <p className="composer-err" role="alert">{err}</p>}
      <div className="composer-actions">
        <button className="btn-save" disabled={busy || !f.docNumber.trim() || !f.title.trim()}
                onClick={save}>{busy ? 'Creating' : 'Create draft'}</button>
      </div>
    </div>
  );
}

function DocumentCard({ doc, people, me, onChanged }: {
  doc: ControlledDocument; people: Colleague[]; me: string | null; onChanged: () => void;
}) {
  const [approver, setApprover] = useState('');
  const [statement, setStatement] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const iAmApprover = doc.approver_id === me;
  const iAmOwner = doc.owner_id === me;

  async function submit() {
    setErr(null);
    try { await submitForApproval(doc.id, approver); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }
  async function approve() {
    setErr(null);
    try { await approveDocument(doc.id, statement); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <article className={`card q-doc q-${doc.status}`}>
      <header className="q-doc-head">
        <div>
          <span className="mono q-num">{doc.doc_number}</span>
          <h3>{doc.title}</h3>
        </div>
        <div className="q-tags">
          <span className={`pill ${doc.status === 'approved' ? 'ok'
            : doc.status === 'in_review' ? 'warn' : 'mute'}`}>{doc.status.replace('_', ' ')}</span>
          <span className="pill mute">v{doc.version}</span>
          {doc.training_required && <span className="pill info">read &amp; sign</span>}
        </div>
      </header>

      <dl className="q-meta">
        <dt>Author</dt><dd>{doc.owner_name ?? '—'}</dd>
        <dt>Approver</dt><dd>{doc.approver_name ?? 'not yet nominated'}</dd>
        {doc.approved_at && (<><dt>Approved</dt>
          <dd>{new Date(doc.approved_at).toLocaleDateString('en-GB')}</dd></>)}
        {doc.next_review_due && (<><dt>Next review</dt>
          <dd>{new Date(doc.next_review_due).toLocaleDateString('en-GB')}</dd></>)}
      </dl>

      {doc.approval_statement && (
        <p className="q-statement">&ldquo;{doc.approval_statement}&rdquo;</p>
      )}

      {/* Submit for authorisation. The author is excluded from the list because the database
          refuses that combination, and offering an option that will be refused is unkind. */}
      {doc.status === 'draft' && iAmOwner && (
        <div className="q-action">
          <select value={approver} onChange={(e) => setApprover(e.target.value)}>
            <option value="">Choose an approver…</option>
            {people.filter((p) => p.id !== doc.owner_id).map((p) => (
              <option key={p.id} value={p.id}>{p.full_name ?? p.email}</option>
            ))}
          </select>
          <button className="btn-save" disabled={!approver} onClick={submit}>
            Send for authorisation
          </button>
        </div>
      )}

      {doc.status === 'in_review' && iAmApprover && (
        <div className="q-action q-approve">
          <textarea rows={2} value={statement}
                    placeholder="State what you are attesting to. Required."
                    onChange={(e) => setStatement(e.target.value)} />
          <button className="btn-save" disabled={statement.trim().length < 10} onClick={approve}>
            Authorise
          </button>
        </div>
      )}

      {doc.status === 'in_review' && !iAmApprover && (
        <p className="q-waiting">Waiting on {doc.approver_name ?? 'the named approver'}.</p>
      )}

      {err && <p className="composer-err" role="alert">{err}</p>}

      {/* An approved document is locked: its file is the record, and swapping it after
          authorisation would make the approval statement refer to something that no longer
          exists. Revising means a new version, which is the point of version control. */}
      <Attachments
        objectType="document" objectId={doc.id} bucket="quality"
        label="Document file" roles={['attachment', 'signed']}
        readOnly={doc.status === 'approved' || doc.status === 'superseded'}
      />
    </article>
  );
}

function CapaTab({ ncs, capas, docs, onChanged }: {
  ncs: Nonconformance[]; capas: Capa[]; docs: ControlledDocument[]; onChanged: () => void;
}) {
  const [adding, setAdding] = useState<'nc' | 'capa' | null>(null);
  const [n, setN] = useState({ reference: '', title: '', description: '', source: 'process',
    severity: 'minor', detectedOn: new Date().toISOString().slice(0, 10), immediateAction: '' });
  const [c, setC] = useState({ reference: '', title: '', nonconformanceId: '',
    capaType: 'corrective', problemStatement: '', targetClose: '', effectivenessCheckDue: '' });
  const [err, setErr] = useState<string | null>(null);

  const openNcs = ncs.filter((x) => x.status !== 'closed' && x.status !== 'not_a_nc');

  return (
    <>
      <div className="g-call-actions">
        <button className="btn-compose" onClick={() => setAdding(adding === 'nc' ? null : 'nc')}>
          Raise a nonconformance
        </button>
        <button className="btn-compose" onClick={() => setAdding(adding === 'capa' ? null : 'capa')}>
          Raise a CAPA
        </button>
      </div>

      {adding === 'nc' && (
        <div className="composer card q-form">
          <div className="composer-row">
            <input placeholder="NC-001" value={n.reference} style={{ maxWidth: '8rem' }}
                   onChange={(e) => setN({ ...n, reference: e.target.value })} />
            <input placeholder="What went wrong" className="grow" value={n.title}
                   onChange={(e) => setN({ ...n, title: e.target.value })} />
          </div>
          <div className="composer-row">
            <select value={n.severity} onChange={(e) => setN({ ...n, severity: e.target.value })}>
              {NC_SEVERITY.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <select value={n.source} onChange={(e) => setN({ ...n, source: e.target.value })}>
              {NC_SOURCE.map((v) => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
            </select>
            <label className="composer-ack">Detected
              <input type="date" value={n.detectedOn}
                     onChange={(e) => setN({ ...n, detectedOn: e.target.value })} />
            </label>
          </div>
          <textarea rows={2} placeholder="Description (required)" value={n.description}
                    onChange={(e) => setN({ ...n, description: e.target.value })} />
          {/* Containment first. What did you do straight away to stop it getting worse is a
              different question from what you will do to stop it happening again. */}
          <textarea rows={2} placeholder="Immediate action taken to contain it"
                    value={n.immediateAction}
                    onChange={(e) => setN({ ...n, immediateAction: e.target.value })} />
          {err && <p className="composer-err">{err}</p>}
          <div className="composer-actions">
            <button className="btn-save"
                    disabled={!n.reference.trim() || !n.title.trim() || n.description.trim().length < 10}
                    onClick={() => raiseNonconformance(n)
                      .then(() => { setAdding(null); onChanged(); })
                      .catch((e) => setErr(String(e.message ?? e)))}>Raise</button>
          </div>
        </div>
      )}

      {adding === 'capa' && (
        <div className="composer card q-form">
          <div className="composer-row">
            <input placeholder="CAPA-001" value={c.reference} style={{ maxWidth: '8rem' }}
                   onChange={(e) => setC({ ...c, reference: e.target.value })} />
            <input placeholder="Title" className="grow" value={c.title}
                   onChange={(e) => setC({ ...c, title: e.target.value })} />
            <select value={c.nonconformanceId}
                    onChange={(e) => setC({ ...c, nonconformanceId: e.target.value })}>
              <option value="">Not linked to an NC</option>
              {ncs.map((x) => <option key={x.id} value={x.id}>{x.reference}: {x.title}</option>)}
            </select>
          </div>
          <div className="composer-row">
            {/* Corrective fixes what happened; preventive stops what has not happened yet. A
                system that cannot tell them apart cannot show it does the second at all. */}
            <select value={c.capaType} onChange={(e) => setC({ ...c, capaType: e.target.value })}>
              {CAPA_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <textarea rows={2} placeholder="Problem statement (required)" value={c.problemStatement}
                    onChange={(e) => setC({ ...c, problemStatement: e.target.value })} />
          <div className="composer-row">
            <label className="composer-ack">Target close
              <input type="date" value={c.targetClose}
                     onChange={(e) => setC({ ...c, targetClose: e.target.value })} />
            </label>
            {/* Set NOW, before the work is done. Deciding when you will check that a fix
                worked, in advance, is what stops the check quietly never happening. */}
            <label className="composer-ack">Effectiveness check due
              <input type="date" value={c.effectivenessCheckDue}
                     onChange={(e) => setC({ ...c, effectivenessCheckDue: e.target.value })} />
            </label>
          </div>
          {err && <p className="composer-err">{err}</p>}
          <div className="composer-actions">
            <button className="btn-save"
                    disabled={!c.reference.trim() || !c.title.trim() || c.problemStatement.trim().length < 10}
                    onClick={() => raiseCapa(c)
                      .then(() => { setAdding(null); onChanged(); })
                      .catch((e) => setErr(String(e.message ?? e)))}>Raise CAPA</button>
          </div>
        </div>
      )}

      {openNcs.length === 0 && capas.length === 0 && !adding && (
        <p className="block-empty">
          Nothing open. A quality system with no recorded nonconformances is usually one
          nobody is using, so this being empty is worth a thought rather than a celebration.
        </p>
      )}

      {openNcs.map((x) => (
        <article key={x.id} className="card q-doc q-in_review">
          <header className="q-doc-head">
            <div>
              <span className="mono q-num">{x.reference}</span>
              <h3>{x.title}</h3>
              <div className="g-app-sub">
                {x.source.replace(/_/g, ' ')} · detected {new Date(x.detected_on).toLocaleDateString('en-GB')}
              </div>
            </div>
            <div className="q-tags">
              <span className={`pill ${x.severity === 'critical' ? 'risk'
                : x.severity === 'major' ? 'warn' : 'mute'}`}>{x.severity}</span>
              <select value={x.status}
                      onChange={(e) => updateNonconformance(x.id, { status: e.target.value }).then(onChanged)}>
                {NC_STATUS.map((v) => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </header>
          {x.description && <p className="notice-body">{x.description}</p>}
          {x.immediate_action && (
            <p className="q-statement">Contained: {x.immediate_action}</p>
          )}
          {(x.capas ?? []).length > 0 && (
            <div className="g-app-meta">
              {(x.capas ?? []).map((k) => (
                <span key={k.id} className="pill accent">{k.reference} · {k.status.replace(/_/g, ' ')}</span>
              ))}
            </div>
          )}
        </article>
      ))}

      {capas.map((k) => <CapaCard key={k.id} capa={k} docs={docs} onChanged={onChanged} />)}
    </>
  );
}

function CapaCard({ capa, docs, onChanged }: {
  capa: Capa; docs: ControlledDocument[]; onChanged: () => void;
}) {
  const [action, setAction] = useState('');
  const [evidence, setEvidence] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const done = (capa.actions ?? []).filter((a) => a.status === 'done').length;
  const total = (capa.actions ?? []).length;

  return (
    <article className={`card q-doc ${capa.effectiveness_verified ? 'q-approved' : 'q-draft'}`}>
      <header className="q-doc-head">
        <div>
          <span className="mono q-num">{capa.reference}</span>
          <h3>{capa.title}</h3>
          <div className="g-app-sub">
            raised {new Date(capa.raised_on).toLocaleDateString('en-GB')}
            {capa.owner_name && ` · ${capa.owner_name}`}
            {total > 0 && ` · ${done}/${total} actions done`}
          </div>
        </div>
        <div className="q-tags">
          <span className={`pill ${capa.status === 'effective' ? 'ok'
            : capa.status === 'ineffective' ? 'risk' : 'mute'}`}>
            {capa.status.replace(/_/g, ' ')}
          </span>
          <select value={capa.status}
                  onChange={(e) => updateCapa(capa.id, { status: e.target.value }).then(onChanged)}>
            {CAPA_STATUS.map((v) => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
      </header>

      {capa.problem_statement && <p className="notice-body">{capa.problem_statement}</p>}

      {/* Root cause is a text field on purpose. "Human error" is not a root cause, and a
          dropdown of tidy options would invite exactly that answer. */}
      <label className="cr-field">
        <span>Root cause: why it happened, not what happened</span>
        <textarea rows={2} defaultValue={capa.root_cause ?? ''}
                  onBlur={(e) => e.target.value !== (capa.root_cause ?? '')
                    && updateCapa(capa.id, { root_cause: e.target.value }).then(onChanged)} />
      </label>

      <ul className="cr-reqs">
        {(capa.actions ?? []).sort((a, b) => a.sort_order - b.sort_order).map((a) => (
          <li key={a.id}>
            <input type="checkbox" checked={a.status === 'done'}
                   onChange={(e) => updateCapaAction(a.id, { status: e.target.checked ? 'done' : 'open' })
                     .then(onChanged)} />
            <span style={{ textDecoration: a.status === 'done' ? 'line-through' : 'none' }}>
              {a.description}
            </span>
            {a.due_on && <span className="muted">due {new Date(a.due_on).toLocaleDateString('en-GB')}</span>}
          </li>
        ))}
      </ul>

      <div className="q-action">
        <input placeholder="Add an action" value={action} style={{ flex: 1 }}
               onChange={(e) => setAction(e.target.value)} />
        <button className="btn-save" disabled={!action.trim()}
                onClick={() => addCapaAction({ capaId: capa.id, description: action,
                  sortOrder: total })
                  .then(() => { setAction(''); onChanged(); })}>Add</button>
      </div>

      {/* A CAPA changes a procedure. Recording WHICH one is what lets an auditor asking
          "show me what changed as a result" get an answer rather than a shrug. */}
      <div className="q-action">
        <label className="composer-ack" style={{ flex: 1 }}>
          Resulting document
          <select value={capa.resulting_document_id ?? ''}
                  onChange={(e) => updateCapa(capa.id, {
                    resulting_document_id: e.target.value || null }).then(onChanged)}>
            <option value="">none recorded</option>
            {docs.map((d) => (
              <option key={d.id} value={d.id}>{d.doc_number}: {d.title}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Effectiveness is a separate step from closure. "Closed" without a verified check is
          the single most common finding in a quality system audit. */}
      {capa.effectiveness_verified == null && (
        <div className="q-action q-approve">
          <textarea rows={2} value={evidence}
                    placeholder="Effectiveness evidence: what did you check, and what did it show?"
                    onChange={(e) => setEvidence(e.target.value)} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
            <button className="btn-save" disabled={evidence.trim().length < 10}
                    onClick={() => verifyEffectiveness(capa.id, true, evidence)
                      .then(onChanged).catch((e) => setErr(String(e.message ?? e)))}>
              Effective
            </button>
            <button className="btn-cancel" disabled={evidence.trim().length < 10}
                    onClick={() => verifyEffectiveness(capa.id, false, evidence)
                      .then(onChanged).catch((e) => setErr(String(e.message ?? e)))}>
              Not effective
            </button>
          </div>
        </div>
      )}

      {capa.effectiveness_verified != null && (
        <p className={`q-statement`}>
          {capa.effectiveness_verified ? 'Verified effective' : 'Verified NOT effective'}
          {capa.effectiveness_verified_on && ` on ${new Date(capa.effectiveness_verified_on).toLocaleDateString('en-GB')}`}
          {capa.effectiveness_evidence && `: ${capa.effectiveness_evidence}`}
        </p>
      )}

      {err && <p className="composer-err" role="alert">{err}</p>}
    </article>
  );
}

function Register({ rows, me, onChanged }: {
  rows: RegisterRow[]; me: string | null; onChanged: () => void;
}) {
  const [signing, setSigning] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);

  async function sign(documentId: string) {
    setErr(null);
    try { await acknowledgeDocument(documentId, name); setSigning(null); setName(''); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  if (!rows.length) {
    return <p className="block-empty">
      Nothing to sign. Only approved documents marked read-and-sign appear here.
    </p>;
  }

  return (
    <>
      {err && <p className="block-error" role="alert">{err}</p>}
      <table className="data">
        <thead>
          <tr><th>Document</th><th>Person</th><th>Signed</th><th /></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.document_id}-${r.member_id}`} className={r.outstanding ? '' : 'row-done'}>
              <td><span className="mono">{r.doc_number}</span> {r.title}</td>
              <td>{r.full_name}</td>
              <td>{r.acknowledged_at
                ? new Date(r.acknowledged_at).toLocaleDateString('en-GB')
                : <span className="pill warn">outstanding</span>}</td>
              <td className="num">
                {r.outstanding && r.member_id === me && (
                  signing === r.document_id ? (
                    <span className="ack-sign">
                      <input placeholder="Type your full name" value={name}
                             onChange={(e) => setName(e.target.value)} />
                      <button disabled={name.trim().length < 3}
                              onClick={() => sign(r.document_id)}>Sign</button>
                    </span>
                  ) : (
                    <button className="link-btn" onClick={() => setSigning(r.document_id)}>
                      Read &amp; sign
                    </button>
                  )
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}



export function CrewView() {
  /* Four separate answers, because the module contains four different jobs. Held apart so a
     control is hidden when this user cannot use it, rather than shown and then refused. */
  const [gates, setGates] = useState<{ view: boolean; admin: boolean; access: boolean;
                                       grant: boolean } | null>(null);
  const allowed = gates?.view ?? null;
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [roles, setRoles] = useState<CrewRole[]>([]);
  const [comps, setComps] = useState<Competence[]>([]);
  const [tab, setTab] = useState<'people' | 'org' | 'roles' | 'goals' | 'access'>('people');
  const [chart, setChart] = useState<OrgPosition[]>([]);
  const [units, setUnits] = useState<OrgUnit[]>([]);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [members, setMembers] = useState<Member[]>([]);
  const reload = () => setNonce((n) => n + 1);

  useEffect(() => { crewGates().then(setGates); }, []);
  useEffect(() => {
    if (!gates?.admin) return;
    listMembers().then(setMembers).catch(onFail('Members', () => setMembers([])));
  }, [gates?.admin, nonce]);
  useEffect(() => {
    if (!allowed) return;
    listCrew().then(setCrew).catch((e) => setErr(String(e.message ?? e)));
    listRoles().then(setRoles).catch(onFail('Crew permissions', () => setRoles([])));
    listCompetences().then(setComps).catch(onFail('The crew list', () => setComps([])));
    fetchOrgChart().then(setChart).catch(onFail('The crew list', () => setChart([])));
    fetchUnits().then(setUnits).catch(onFail('The crew list', () => setUnits([])));
  }, [allowed, nonce]);

  if (allowed === null) return <div className="page" />;

  // Presentation only. Someone who reaches this page another way is still refused by the
  // database on every single call.
  if (!allowed) {
    return (
      <div className="page">
        <div className="page-head">
          <h1 className="page-title">Crew</h1>
          <p className="page-sub">
            Workforce records are restricted to the owner, an administrator, HR and the senior
            independent director. This is enforced in the database, so there is nothing to see
            here even with the page open.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Crew</h1>
        <p className="page-sub">
          People, the roles they hold and the competences those roles require. A gap is a
          required competence that is missing or whose evidence has expired: a lapsed
          competence is a gap, not history, which is the distinction an auditor tests.
        </p>
      </div>

      {err && <p className="block-error" role="alert">{err}</p>}

      <div className="q-tabs">
        <button className={tab === 'people' ? 'on' : ''} onClick={() => setTab('people')}>
          People ({crew.length})
        </button>
        <button className={tab === 'org' ? 'on' : ''} onClick={() => setTab('org')}>
          Org structure ({chart.filter((p) => p.status === 'filled').length}/{chart.length} filled)
        </button>
        <button className={tab === 'roles' ? 'on' : ''} onClick={() => setTab('roles')}>
          Roles and competences ({roles.length})
        </button>
        <button className={tab === 'goals' ? 'on' : ''} onClick={() => setTab('goals')}>
          Goals and appraisal
        </button>
        {/* Hidden rather than shown-and-refused. HR administers the workforce; who may sign in
            is a different question with a different answer, and the database gives it. */}
        {gates?.access && (
          <button className={tab === 'access' ? 'on' : ''} onClick={() => setTab('access')}>
            Access
          </button>
        )}
        {tab === 'people' && gates?.admin && (
          <button className="btn-compose q-new" onClick={() => setAdding(!adding)}>
            {adding ? 'Cancel' : 'Add colleague'}
          </button>
        )}
      </div>

      {tab === 'people' && (
        <>
          {adding && <AddCrew onDone={() => { setAdding(false); reload(); }} />}
          {crew.length === 0 && !adding && (
            <p className="block-empty">Nobody recorded yet.</p>
          )}
          <table className="data">
            <thead>
              <tr><th>Name</th><th>Job title</th><th>Role</th>
                  {/* The reporting line drives who approves this person's expenses (0091):
                      their line manager, unless an explicit approver is set, and the owner
                      and the board always go to the owner. */}
                  <th>Reports to</th><th>Expenses approved by</th><th>Status</th>
                  <th className="num">Gaps</th><th /></tr>
            </thead>
            <tbody>
              {crew.map((m) => (
                <tr key={m.out_employee_id}>
                  <td><strong>{m.out_name}</strong></td>
                  <td>{m.out_job_title ?? '—'}</td>
                  <td>{m.out_primary_role ?? <span className="muted">no role assigned</span>}</td>
                  <td>
                    <select className="input sel" value={m.out_reports_to_id ?? ''}
                            onChange={(e) => setReportingLine(m.out_employee_id, e.target.value || null)
                              .then(reload).catch(onFail('Reporting line'))}>
                      <option value="">nobody recorded</option>
                      {crew.filter((c) => c.out_employee_id !== m.out_employee_id).map((c) => (
                        <option key={c.out_employee_id} value={c.out_employee_id}>{c.out_name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {m.out_member_id ? (
                      <select className="input sel" value={m.out_expense_approver_id ?? ''}
                              onChange={(e) => setExpenseApprover(m.out_member_id!, e.target.value || null)
                                .then(reload).catch(onFail('Expense approver'))}>
                        <option value="">
                          {m.out_reports_to_id ? `line manager (${m.out_reports_to_name})` : 'the owner (no line manager)'}
                        </option>
                        {members.filter((x) => x.id !== m.out_member_id && x.status === 'active').map((x) => (
                          <option key={x.id} value={x.id}>{x.full_name ?? x.email}</option>
                        ))}
                      </select>
                    ) : <span className="muted" title="No login linked to this employee record.">no login</span>}
                  </td>
                  <td><span className={`pill ${m.out_status === 'active' ? 'ok' : 'mute'}`}>
                    {m.out_status}</span></td>
                  <td className="num">
                    {m.out_gaps > 0
                      ? <span className="pill warn">{m.out_gaps}</span>
                      : <span className="muted">0</span>}
                  </td>
                  <td className="num">
                    <button className="link-btn" onClick={() => setOpenId(m.out_employee_id)}>
                      Open record
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {openId && (
            <RecordDrawer employeeId={openId} roles={roles}
                          onClose={() => setOpenId(null)} onChanged={reload} />
          )}
        </>
      )}

      {tab === 'org' && (
        <OrgTab chart={chart} units={units} crew={crew} onChanged={reload} />
      )}

      {tab === 'roles' && (
        <RolesTab roles={roles} comps={comps} onChanged={reload} />
      )}

      {/* Opens on your own objectives. Someone with hr or owner can open a colleague's from
          their record; everyone else sees only their own, enforced in the database. */}
      {tab === 'goals' && <GoalsPanel />}

      {tab === 'access' && gates?.access && <AccessPanel canGrantRoles={!!gates.grant} />}
    </div>
  );
}

function OrgTab({ chart, units, crew, onChanged }: {
  chart: OrgPosition[]; units: OrgUnit[]; crew: CrewMember[]; onChanged: () => void;
}) {
  const [adding, setAdding] = useState<'unit' | 'position' | null>(null);
  const [filling, setFilling] = useState<string | null>(null);
  const [who, setWho] = useState({ employeeId: '', holderName: '' });
  const [u, setU] = useState({ key: '', name: '', unitType: 'function', purpose: '' });
  const [pos, setPos] = useState({ unitId: '', key: '', title: '', remit: '',
    seniority: 'senior', engagement: 'employee', headcount: 1 });
  const [err, setErr] = useState<string | null>(null);

  const byUnit = chart.reduce<Record<string, OrgPosition[]>>((a, p) => {
    (a[p.unit] ||= []).push(p); return a;
  }, {});

  async function fill(positionId: string) {
    setErr(null);
    try {
      await assignHolder({
        positionId,
        employeeId: who.employeeId || null,
        holderName: who.employeeId ? undefined : who.holderName,
      });
      setFilling(null); setWho({ employeeId: '', holderName: '' }); onChanged();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <>
      <div className="g-call-actions">
        <button className="btn-compose" onClick={() => setAdding(adding === 'unit' ? null : 'unit')}>
          Add unit
        </button>
        <button className="btn-compose" disabled={!units.length}
                onClick={() => setAdding(adding === 'position' ? null : 'position')}>
          Add position
        </button>
        <span className="g-hint">
          A seat exists whether or not anyone holds it. Vacancies are the useful part.
        </span>
      </div>

      {adding === 'unit' && (
        <div className="composer card q-form">
          <div className="composer-row">
            <input placeholder="Key, e.g. finance" value={u.key} style={{ maxWidth: '9rem' }}
                   onChange={(e) => setU({ ...u, key: e.target.value })} />
            <input placeholder="Unit name" className="grow" value={u.name}
                   onChange={(e) => setU({ ...u, name: e.target.value })} />
            <select value={u.unitType} onChange={(e) => setU({ ...u, unitType: e.target.value })}>
              {UNIT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <input placeholder="Purpose" value={u.purpose}
                 onChange={(e) => setU({ ...u, purpose: e.target.value })} />
          <div className="composer-actions">
            <button className="btn-save" disabled={!u.key.trim() || !u.name.trim()}
                    onClick={() => addUnit(u).then(() => { setAdding(null); setU({ key: '', name: '', unitType: 'function', purpose: '' }); onChanged(); })
                      .catch((e) => setErr(String(e.message ?? e)))}>Add unit</button>
          </div>
        </div>
      )}

      {adding === 'position' && (
        <div className="composer card q-form">
          <div className="composer-row">
            <select value={pos.unitId} onChange={(e) => setPos({ ...pos, unitId: e.target.value })}>
              <option value="">Unit…</option>
              {units.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
            <input placeholder="Key" value={pos.key} style={{ maxWidth: '8rem' }}
                   onChange={(e) => setPos({ ...pos, key: e.target.value })} />
            <input placeholder="Title" className="grow" value={pos.title}
                   onChange={(e) => setPos({ ...pos, title: e.target.value })} />
          </div>
          <div className="composer-row">
            <select value={pos.seniority} onChange={(e) => setPos({ ...pos, seniority: e.target.value })}>
              {SENIORITY.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            {/* Engagement is not decoration. A non-executive or advisor is paid a fee, and
                that payment is a disclosable transfer of value; an employee's is not. */}
            <select value={pos.engagement} onChange={(e) => setPos({ ...pos, engagement: e.target.value })}>
              {ENGAGEMENT.map((x) => <option key={x} value={x}>{x.replace('_', ' ')}</option>)}
            </select>
            <label className="composer-ack">Seats
              <input type="number" min={1} value={pos.headcount} style={{ width: '4rem' }}
                     onChange={(e) => setPos({ ...pos, headcount: Number(e.target.value) })} />
            </label>
          </div>
          <input placeholder="Remit" value={pos.remit}
                 onChange={(e) => setPos({ ...pos, remit: e.target.value })} />
          <div className="composer-actions">
            <button className="btn-save" disabled={!pos.unitId || !pos.key.trim() || !pos.title.trim()}
                    onClick={() => addPosition(pos).then(() => { setAdding(null); onChanged(); })
                      .catch((e) => setErr(String(e.message ?? e)))}>Add position</button>
          </div>
        </div>
      )}

      {err && <p className="block-error" role="alert">{err}</p>}

      {Object.entries(byUnit).map(([unit, positions]) => (
        <section key={unit} className="org-unit">
          <h3 className="org-unit-name">
            {unit}
            <span className="pill mute">{positions[0].unit_type}</span>
          </h3>
          {positions.map((p) => (
            <article key={p.id} className={`card org-pos org-${p.status}`}
                     style={{ marginLeft: `${Math.min(p.depth - 1, 3) * 1.5}rem` }}>
              <div className="org-main">
                <div className="org-title">
                  {p.title}
                  {p.headcount > 1 && <span className="pill mute">{p.holders}/{p.headcount} seats</span>}
                </div>
                <div className="org-held">
                  {p.holders > 0
                    ? <strong>{p.held_by}</strong>
                    : <span className="pill warn">{p.status === 'planned' ? 'planned' : 'vacant'}</span>}
                  {p.engagement && p.engagement !== 'employee' && (
                    <span className="pill info">{p.engagement.replace('_', ' ')}</span>
                  )}
                </div>
                {p.remit && <p className="org-remit">{p.remit}</p>}
              </div>
              <div className="org-actions">
                {filling === p.id ? (
                  <div className="q-action">
                    <select value={who.employeeId}
                            onChange={(e) => setWho({ ...who, employeeId: e.target.value })}>
                      <option value="">Someone outside the payroll…</option>
                      {crew.map((c) => (
                        <option key={c.out_employee_id} value={c.out_employee_id}>{c.out_name}</option>
                      ))}
                    </select>
                    {/* Directors and advisors are not employees and must not be given an
                        employment record just to appear on a chart. */}
                    {!who.employeeId && (
                      <input placeholder="Name" value={who.holderName}
                             onChange={(e) => setWho({ ...who, holderName: e.target.value })} />
                    )}
                    <button className="btn-save"
                            disabled={!who.employeeId && who.holderName.trim().length < 2}
                            onClick={() => fill(p.id)}>Assign</button>
                    <button className="btn-cancel" onClick={() => setFilling(null)}>Cancel</button>
                  </div>
                ) : (
                  <>
                    <button className="link-btn" onClick={() => setFilling(p.id)}>
                      {p.holders > 0 ? 'Add holder' : 'Assign'}
                    </button>
                    <select value={p.status}
                            onChange={(e) => updatePosition(p.id, { status: e.target.value }).then(onChanged)}>
                      <option value="planned">planned</option>
                      <option value="vacant">vacant</option>
                      <option value="filled">filled</option>
                      <option value="closed">closed</option>
                    </select>
                  </>
                )}
              </div>
            </article>
          ))}
        </section>
      ))}
    </>
  );
}

function AddCrew({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({
    firstName: '', lastName: '', jobTitle: '', department: '',
    startDate: new Date().toISOString().slice(0, 10), employmentType: 'employee',
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setErr(null); setBusy(true);
    try { await addCrew(f); onDone(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="composer card q-form">
      <div className="composer-row">
        <input placeholder="First name" value={f.firstName} disabled={busy}
               onChange={(e) => setF({ ...f, firstName: e.target.value })} />
        <input placeholder="Last name" value={f.lastName} disabled={busy}
               onChange={(e) => setF({ ...f, lastName: e.target.value })} />
      </div>
      <div className="composer-row">
        <input placeholder="Job title" className="grow" value={f.jobTitle} disabled={busy}
               onChange={(e) => setF({ ...f, jobTitle: e.target.value })} />
        <input placeholder="Department" value={f.department} disabled={busy}
               onChange={(e) => setF({ ...f, department: e.target.value })} />
        <label className="composer-ack">Starts
          <input type="date" value={f.startDate} disabled={busy}
                 onChange={(e) => setF({ ...f, startDate: e.target.value })} />
        </label>
        <select value={f.employmentType} disabled={busy}
                onChange={(e) => setF({ ...f, employmentType: e.target.value })}>
          <option value="employee">Employee</option>
          <option value="contractor">Contractor</option>
          <option value="consultant">Consultant</option>
        </select>
      </div>
      {err && <p className="composer-err" role="alert">{err}</p>}
      <p className="correction-note">
        This creates the employment record only. A login is granted separately: joining the
        payroll and gaining access to the commercial pipeline are two different decisions.
      </p>
      <div className="composer-actions">
        <button className="btn-save" disabled={busy || !f.firstName.trim() || !f.lastName.trim()}
                onClick={save}>{busy ? 'Adding' : 'Add colleague'}</button>
      </div>
    </div>
  );
}

/* The sensitive record. Opening it is logged, which is why it is a deliberate click and not
   an expanding row. */
function RecordDrawer({ employeeId, roles, onClose, onChanged }: {
  employeeId: string; roles: CrewRole[]; onClose: () => void; onChanged: () => void;
}) {
  const [rec, setRec] = useState<EmployeeRecord | null>(null);
  const [history, setHistory] = useState<AccessEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [patch, setPatch] = useState<Record<string, unknown>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [assign, setAssign] = useState('');
  const dialogRef = useDialog(onClose);

  useEffect(() => {
    openRecord(employeeId).then(setRec).catch((e) => setErr(String(e.message ?? e)));
    accessHistory(employeeId).then(setHistory).catch(onFail('Record access history', () => setHistory([])));
  }, [employeeId]);

  /* The record is returned with out_ prefixes but written back under real column names, so
     a field needs to know both. Reading the patch under the wrong key was why typing
     registered as a pending change but never appeared in the box. */
  const COLUMN: Record<string, keyof EmployeeRecord> = {
    legal_first_name: 'out_legal_first', legal_last_name: 'out_legal_last',
    preferred_name: 'out_preferred_name', employee_number: 'out_employee_number',
    job_title: 'out_job_title', department: 'out_department',
    work_location: 'out_work_location', start_date: 'out_start_date',
    fte: 'out_fte', contracted_hours: 'out_contracted_hours',
    holiday_entitlement_days: 'out_holiday_days',
    annual_salary: 'out_annual_salary', salary_currency: 'out_salary_currency',
    tax_code: 'out_tax_code', payroll_id_sage: 'out_payroll_id',
    pension_scheme: 'out_pension_scheme',
    date_of_birth: 'out_date_of_birth', ni_number: 'out_ni_number',
    personal_email: 'out_personal_email', personal_phone: 'out_personal_phone',
    rtw_check_date: 'out_rtw_check_date', visa_type: 'out_visa_type',
    visa_expiry: 'out_visa_expiry', notice_period_days: 'out_notice_days',
  };

  function set(column: string, value: unknown) {
    setPatch((p) => ({ ...p, [column]: value }));
  }

  /** Bind an input to a column: shows the edit if there is one, else the stored value. */
  function bind(column: string) {
    const outKey = COLUMN[column];
    const pending = patch[column];
    const stored = outKey ? (rec?.[outKey] as string | number | null) : null;
    return {
      v: (pending ?? stored ?? '') as string | number,
      onChange: (x: string) => set(column, x),
    };
  }

  async function save() {
    setErr(null); setBusy(true);
    try {
      await updateRecord(employeeId, patch);
      setPatch({});
      setRec(await openRecord(employeeId));
      onChanged();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="drawer card cr-drawer" role="dialog" aria-modal="true"
             aria-label="Employee record" ref={dialogRef}>
        <header className="drawer-head">
          <div>
            <div className="drawer-eyebrow">Employee record</div>
            <h2 className="drawer-title display">
              {rec ? `${rec.out_legal_first} ${rec.out_legal_last}` : 'Opening…'}
            </h2>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>

        {err && <p className="block-error" role="alert">{err}</p>}

        {rec && (
          <div className="drawer-body cr-body">
            <section>
              <div className="label">Employment</div>
              <div className="cr-grid">
                <Field label="First name" {...bind('legal_first_name')} />
                <Field label="Known as" {...bind('preferred_name')} />
                <Field label="Last name" {...bind('legal_last_name')} />
                <Field label="Job title" {...bind('job_title')} />
                <Field label="Department" {...bind('department')} />
                <Field label="Start date" type="date" {...bind('start_date')} />
                <Field label="Employee no." {...bind('employee_number')} />
                <Field label="FTE" type="number" {...bind('fte')} />
                <Field label="Holiday days" type="number" {...bind('holiday_entitlement_days')} />
              </div>
            </section>

            <section>
              <div className="label">Pay</div>
              <div className="cr-grid">
                <Field label="Annual salary" type="number" {...bind('annual_salary')} />
                <Field label="Currency" {...bind('salary_currency')} />
                <Field label="Tax code" {...bind('tax_code')} />
                <Field label="Payroll ID (Sage)" {...bind('payroll_id_sage')} />
                <Field label="Pension scheme" {...bind('pension_scheme')} />
              </div>
            </section>

            <section>
              <div className="label">Personal</div>
              <div className="cr-grid">
                <Field label="Date of birth" type="date" {...bind('date_of_birth')} />
                <Field label="National insurance" {...bind('ni_number')} />
                <Field label="Personal email" {...bind('personal_email')} />
                <Field label="Personal phone" {...bind('personal_phone')} />
              </div>
              {/* Address is structured, not one string: a payroll file or a right-to-work
                  pack needs the parts separately. */}
              <div className="cr-grid">
                <Field label="Address line 1" v={(patch['home_address'] as any)?.line1 ?? rec.out_home_address?.line1 ?? ''}
                       onChange={(x) => set('home_address', { ...(rec.out_home_address ?? {}), ...(patch['home_address'] as any ?? {}), line1: x })} />
                <Field label="Town" v={(patch['home_address'] as any)?.town ?? rec.out_home_address?.town ?? ''}
                       onChange={(x) => set('home_address', { ...(rec.out_home_address ?? {}), ...(patch['home_address'] as any ?? {}), town: x })} />
                <Field label="Postcode" v={(patch['home_address'] as any)?.postcode ?? rec.out_home_address?.postcode ?? ''}
                       onChange={(x) => set('home_address', { ...(rec.out_home_address ?? {}), ...(patch['home_address'] as any ?? {}), postcode: x })} />
              </div>
            </section>

            <section>
              <div className="label">Right to work</div>
              <div className="cr-grid">
                <label className="composer-ack">
                  <input type="checkbox" checked={Boolean(patch['rtw_checked'] ?? rec.out_rtw_checked)}
                         onChange={(e) => set('rtw_checked', e.target.checked)} />
                  Checked
                </label>
                <Field label="Check date" type="date" {...bind('rtw_check_date')} />
                <Field label="Visa type" {...bind('visa_type')} />
                <Field label="Visa expiry" type="date" {...bind('visa_expiry')} />
              </div>
            </section>

            <section>
              <div className="label">Role</div>
              <div className="q-action">
                <select value={assign} onChange={(e) => setAssign(e.target.value)}>
                  <option value="">Assign a role…</option>
                  {roles.map((r) => <option key={r.out_role_id} value={r.out_role_id}>{r.out_title}</option>)}
                </select>
                <button className="btn-save" disabled={!assign}
                        onClick={() => assignRole(employeeId, assign, true).then(() => { setAssign(''); onChanged(); })}>
                  Assign
                </button>
              </div>
            </section>

            <div className="cr-save">
              <button className="btn-save" disabled={busy || !Object.keys(patch).length} onClick={save}>
                {busy ? 'Saving' : `Save ${Object.keys(patch).length || ''} change${Object.keys(patch).length === 1 ? '' : 's'}`}
              </button>
              <button className="link-btn" onClick={() => setShowHistory(!showHistory)}>
                {showHistory ? 'Hide' : 'Who has seen this record'}
              </button>
            </div>

            {showHistory && (
              <section>
                <div className="label">Access history</div>
                <p className="correction-note">
                  Opening this record wrote an entry here. Field names are recorded on an
                  edit; values never are.
                </p>
                <table className="data compact">
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={i}>
                        <td>{h.out_who}</td>
                        <td>{h.out_action}</td>
                        <td className="muted">{h.out_fields?.join(', ') ?? ''}</td>
                        <td className="num muted">
                          {new Date(h.out_when).toLocaleString('en-GB',
                            { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

function Field({ label, v, onChange, type = 'text' }: {
  label: string; v: string | number; onChange: (v: string) => void; type?: string;
}) {
  return (
    <label className="cr-field">
      <span>{label}</span>
      <input type={type} value={v ?? ''} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function RolesTab({ roles, comps, onChanged }: {
  roles: CrewRole[]; comps: Competence[]; onChanged: () => void;
}) {
  const [sel, setSel] = useState<string | null>(null);
  const [reqs, setReqs] = useState<RoleRequirement[]>([]);
  const [adding, setAdding] = useState<'role' | 'competence' | null>(null);
  const [r, setR] = useState({ code: '', title: '', department: '' });
  const [c, setC] = useState({ code: '', name: '', category: 'technical', validity: '' });
  const [pick, setPick] = useState({ competenceId: '', criticality: 'required' as const, due: 90 });
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!sel) { setReqs([]); return; }
    roleRequirements(sel).then(setReqs).catch(onFail('Role requirements', () => setReqs([])));
  }, [sel]);

  async function attach() {
    setErr(null);
    try {
      await requireCompetence(sel!, pick.competenceId, pick.criticality, pick.due);
      setReqs(await roleRequirements(sel!));
      setPick({ ...pick, competenceId: '' });
      onChanged();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <>
      <div className="g-call-actions">
        <button className="btn-compose" onClick={() => setAdding(adding === 'role' ? null : 'role')}>
          Add role
        </button>
        <button className="btn-compose" onClick={() => setAdding(adding === 'competence' ? null : 'competence')}>
          Add competence
        </button>
      </div>

      {adding === 'role' && (
        <div className="composer card q-form">
          <div className="composer-row">
            <input placeholder="Code, e.g. QA" value={r.code} style={{ maxWidth: '8rem' }}
                   onChange={(e) => setR({ ...r, code: e.target.value })} />
            <input placeholder="Role title" className="grow" value={r.title}
                   onChange={(e) => setR({ ...r, title: e.target.value })} />
            <input placeholder="Department" value={r.department}
                   onChange={(e) => setR({ ...r, department: e.target.value })} />
          </div>
          <div className="composer-actions">
            <button className="btn-save" disabled={!r.code.trim() || !r.title.trim()}
                    onClick={() => addRole(r.code, r.title, r.department)
                      .then(() => { setR({ code: '', title: '', department: '' }); setAdding(null); onChanged(); })
                      .catch((e) => setErr(String(e.message ?? e)))}>Add role</button>
          </div>
        </div>
      )}

      {adding === 'competence' && (
        <div className="composer card q-form">
          <div className="composer-row">
            <input placeholder="Code, e.g. DP-01" value={c.code} style={{ maxWidth: '8rem' }}
                   onChange={(e) => setC({ ...c, code: e.target.value })} />
            <input placeholder="Competence name" className="grow" value={c.name}
                   onChange={(e) => setC({ ...c, name: e.target.value })} />
            <select value={c.category} onChange={(e) => setC({ ...c, category: e.target.value })}>
              <option value="technical">Technical</option>
              <option value="regulatory">Regulatory</option>
              <option value="safety">Safety</option>
              <option value="commercial">Commercial</option>
            </select>
            {/* Validity is what turns a competence into something that lapses. Leave it
                blank only where re-training genuinely is never required. */}
            <label className="composer-ack">Valid for
              <input type="number" placeholder="months" value={c.validity} style={{ width: '5rem' }}
                     onChange={(e) => setC({ ...c, validity: e.target.value })} />
              months
            </label>
          </div>
          <div className="composer-actions">
            <button className="btn-save" disabled={!c.code.trim() || !c.name.trim()}
                    onClick={() => addCompetence(c.code, c.name, c.category, c.validity ? Number(c.validity) : undefined)
                      .then(() => { setC({ code: '', name: '', category: 'technical', validity: '' }); setAdding(null); onChanged(); })
                      .catch((e) => setErr(String(e.message ?? e)))}>Add competence</button>
          </div>
        </div>
      )}

      {err && <p className="block-error" role="alert">{err}</p>}
      {roles.length === 0 && <p className="block-empty">No roles defined yet.</p>}

      {roles.map((role) => (
        <article key={role.out_role_id} className="card q-doc">
          <header className="q-doc-head">
            <div>
              <span className="mono q-num">{role.out_code}</span>
              <h3>{role.out_title}</h3>
            </div>
            <div className="q-tags">
              <span className="pill mute">{role.out_required} required</span>
              <span className="pill mute">{role.out_holders} holding</span>
              <button className="link-btn"
                      onClick={() => setSel(sel === role.out_role_id ? null : role.out_role_id)}>
                {sel === role.out_role_id ? 'Hide' : 'Competences'}
              </button>
            </div>
          </header>

          {sel === role.out_role_id && (
            <>
              {reqs.length === 0 && <p className="att-empty">No competences required yet.</p>}
              <ul className="cr-reqs">
                {reqs.map((q) => (
                  <li key={q.out_competence_id}>
                    <span className="mono">{q.out_code}</span> {q.out_name}
                    <span className={`pill ${q.out_criticality === 'required' ? 'warn' : 'mute'}`}>
                      {q.out_criticality}
                    </span>
                    {q.out_due_within != null && <span className="muted">within {q.out_due_within} days</span>}
                    <button className="att-remove"
                            onClick={() => unrequireCompetence(role.out_role_id, q.out_competence_id)
                              .then(async () => { setReqs(await roleRequirements(role.out_role_id)); onChanged(); })}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>

              <div className="q-action">
                <select value={pick.competenceId}
                        onChange={(e) => setPick({ ...pick, competenceId: e.target.value })}>
                  <option value="">Require a competence…</option>
                  {comps.filter((x) => !reqs.some((q) => q.out_competence_id === x.out_competence_id))
                        .map((x) => <option key={x.out_competence_id} value={x.out_competence_id}>
                          {x.out_code}: {x.out_name}</option>)}
                </select>
                <button className="btn-save" disabled={!pick.competenceId} onClick={attach}>Require</button>
              </div>
            </>
          )}
        </article>
      ))}
    </>
  );
}


export function ChorusView() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchChannels().then(setChannels).catch((e) => setErr(String(e.message ?? e)));
    fetchColleagues().then(setColleagues).catch(onFail('Colleagues', () => setColleagues([])));
  }, [nonce]);

  useEffect(() => {
    if (!sel) { setMessages([]); return; }
    fetchMessages(sel).then(setMessages).catch((e) => setErr(String(e.message ?? e)));
    void markRead(sel);
  }, [sel, nonce]);

  // Newest message in view when the conversation opens or a message arrives. A chat that
  // opens at the top of a year of history is a chat nobody reads.
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages]);

  async function send() {
    if (!sel || !draft.trim()) return;
    setErr(null);
    try {
      await postMessage(sel, draft.trim());
      setDraft('');
      setNonce((n) => n + 1);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  async function openWith(memberId: string) {
    setErr(null);
    try {
      const id = await startDirect(memberId);
      setStarting(false);
      setNonce((n) => n + 1);
      setSel(id);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  const current = channels.find((c) => c.id === sel);

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Chorus</h1>
        <p className="page-sub">
          Conversation anchored to records. The discussion about why a discount was given lives
          on the contract forever instead of scrolling away in a channel. Teams keeps voice,
          video and anything involving people outside the company.
        </p>
      </div>

      {err && <p className="block-error" role="alert">{err}</p>}

      <div className="chorus-layout">
        <div className="thread-list">
          <button className="btn-compose ch-new" onClick={() => setStarting(!starting)}>
            {starting ? 'Cancel' : 'New conversation'}
          </button>

          {starting && (
            <div className="ch-people">
              {colleagues.length === 0 && <p className="block-empty">No colleagues yet.</p>}
              {colleagues.map((m) => (
                <button key={m.id} className="ch-person" onClick={() => openWith(m.id)}>
                  <strong>{m.full_name ?? m.email}</strong>
                  {m.job_title && <span>{m.job_title}</span>}
                </button>
              ))}
            </div>
          )}

          {!starting && channels.length === 0 && (
            <p className="block-empty">
              No conversations. Start one and it appears here for both of you.
            </p>
          )}

          {!starting && channels.map((c) => (
            <button key={c.id} className={`thread ${c.id === sel ? 'sel' : ''}`}
                    onClick={() => setSel(c.id)}>
              <div className="thread-title">
                {c.name ?? c.other_names ?? 'Conversation'}
                {c.unread > 0 && <span className="ch-unread">{c.unread}</span>}
              </div>
              <div className="thread-preview">{c.last_body ?? 'No messages yet'}</div>
              <div className="thread-foot">
                {c.last_message_at
                  ? new Date(c.last_message_at).toLocaleString('en-GB',
                      { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                  : ''}
              </div>
            </button>
          ))}
        </div>

        <div className="card thread-detail ch-detail">
          {!sel && (
            <p className="block-empty">
              Choose a conversation, or start a new one.
            </p>
          )}

          {sel && (
            <>
              <div className="ch-head">
                <h3>{current?.name ?? current?.other_names ?? 'Conversation'}</h3>
              </div>

              <div className="ch-messages">
                {messages.length === 0 && <p className="block-empty">No messages yet. Say something.</p>}
                {messages.map((m) => (
                  <div key={m.id} className={`ch-msg ${m.is_system ? 'sys' : ''}`}>
                    <div className="ch-msg-head">
                      <strong>{m.author_name ?? 'A colleague'}</strong>
                      <span>{new Date(m.created_at).toLocaleString('en-GB',
                        { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      {m.edited_at && <span className="ch-edited">edited</span>}
                    </div>
                    <p>{m.body}</p>
                  </div>
                ))}
                <div ref={endRef} />
              </div>

              <div className="ch-compose">
                <textarea rows={2} value={draft} placeholder="Write a message"
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => {
                            // Enter sends, shift+enter is a new line. The opposite makes a
                            // chat feel like a form.
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
                          }} />
                <button className="btn-save" disabled={!draft.trim()} onClick={send}>Send</button>
              </div>

              <p className="ch-notice">
                Everything written here is a business record: searchable, audited, retained for
                six years, and disclosable in a subject access request. Staff are told this,
                which is the point rather than the small print.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}





export function GrantsView() {
  const [apps, setApps] = useState<Application[]>([]);
  const [calls, setCalls] = useState<Call[]>([]);
  const [funders, setFunders] = useState<Funder[]>([]);
  const [tab, setTab] = useState<'applications' | 'calls' | 'find'>('applications');
  const [contracts, setContracts] = useState<Array<{ id: string; reference: string; title: string }>>([]);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);

  useEffect(() => {
    fetchApplications().then(setApps).catch((e) => setErr(String(e.message ?? e)));
    fetchCalls().then(setCalls).catch(onFail('Funding calls', () => setCalls([])));
    fetchFunders().then(setFunders).catch(onFail('Funders', () => setFunders([])));
    fetchContractsForLinking().then(setContracts).catch(onFail('Contracts', () => setContracts([])));
  }, [nonce]);

  // Open applications first, then settled ones. A rejection stays visible but stops
  // competing for attention with something still winnable.
  const sorted = [...apps].sort((a, b) => {
    const ac = CLOSED.includes(a.status) ? 1 : 0, bc = CLOSED.includes(b.status) ? 1 : 0;
    if (ac !== bc) return ac - bc;
    return (daysUntil(a.call_closes) ?? 9999) - (daysUntil(b.call_closes) ?? 9999);
  });

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Grants</h1>
        <p className="page-sub">
          Applications, calls and the funders behind them. Funders pay in arrears against hard
          windows, and a missed window is usually unrecoverable money, so the closing date is
          what everything here is arranged around.
        </p>
      </div>

      {err && <p className="block-error" role="alert">{err}</p>}

      <div className="q-tabs">
        <button className={tab === 'applications' ? 'on' : ''} onClick={() => setTab('applications')}>
          Applications ({apps.filter((a) => !CLOSED.includes(a.status)).length} open)
        </button>
        <button className={tab === 'calls' ? 'on' : ''} onClick={() => setTab('calls')}>
          Calls ({calls.length})
        </button>
        <button className={tab === 'find' ? 'on' : ''} onClick={() => setTab('find')}>
          Where to look
        </button>
        {tab === 'applications' && (
          <button className="btn-compose q-new" onClick={() => setCreating(!creating)}>
            {creating ? 'Cancel' : 'New application'}
          </button>
        )}
      </div>

      {tab === 'applications' && (
        <>
          {creating && (
            <NewApplication calls={calls} funders={funders}
                            onDone={() => { setCreating(false); reload(); }} />
          )}
          {sorted.length === 0 && !creating && (
            <p className="block-empty">
              No applications yet. Add a call first if you want the closing date tracked
              against it, or create an application on its own.
            </p>
          )}
          {sorted.map((a) => <ApplicationCard key={a.id} app={a} contracts={contracts} onChanged={reload} />)}
        </>
      )}

      {tab === 'calls' && <CallsTab calls={calls} funders={funders} onChanged={reload} />}

      {tab === 'find' && (
        <div className="g-find">
          <p className="block-lead">
            Links into each funder&rsquo;s own search, which is always current. Deliberately not
            an automated scraper: funder sites change layout constantly, an unattended scraper
            fails silently, and a grants module that had quietly stopped finding calls would be
            worse than one that never claimed to.
          </p>
          {FUNDER_SEARCHES.map((f) => (
            <a key={f.url} className="card g-search" href={f.url} target="_blank" rel="noopener noreferrer">
              <strong>{f.name}</strong>
              <span>{f.note}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function NewApplication({ calls, funders, onDone }: {
  calls: Call[]; funders: Funder[]; onDone: () => void;
}) {
  const [f, setF] = useState({
    title: '', callId: '', funderId: '', reference: '',
    requestedAmount: '', currency: 'GBP', leadApplicant: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setErr(null); setBusy(true);
    try {
      await createApplication({
        title: f.title,
        callId: f.callId || null,
        // Choosing a call implies its funder, so the two cannot disagree.
        funderId: f.callId ? (calls.find((c) => c.id === f.callId)?.funder_id ?? null) : (f.funderId || null),
        reference: f.reference,
        requestedAmount: f.requestedAmount ? Number(f.requestedAmount) : undefined,
        currency: f.currency,
        leadApplicant: f.leadApplicant,
      });
      onDone();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="composer card q-form">
      <input placeholder="Application title" value={f.title} disabled={busy}
             onChange={(e) => setF({ ...f, title: e.target.value })} />
      <div className="composer-row">
        <select value={f.callId} disabled={busy}
                onChange={(e) => setF({ ...f, callId: e.target.value })}>
          <option value="">No specific call</option>
          {calls.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}{c.closes_on ? `, closes ${new Date(c.closes_on).toLocaleDateString('en-GB')}` : ''}
            </option>
          ))}
        </select>
        {!f.callId && (
          <select value={f.funderId} disabled={busy}
                  onChange={(e) => setF({ ...f, funderId: e.target.value })}>
            <option value="">Funder…</option>
            {funders.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        )}
      </div>
      <div className="composer-row">
        <input placeholder="Reference" value={f.reference} disabled={busy} style={{ maxWidth: '10rem' }}
               onChange={(e) => setF({ ...f, reference: e.target.value })} />
        <input type="number" placeholder="Amount requested" value={f.requestedAmount} disabled={busy}
               onChange={(e) => setF({ ...f, requestedAmount: e.target.value })} />
        <select value={f.currency} disabled={busy}
                onChange={(e) => setF({ ...f, currency: e.target.value })} style={{ maxWidth: '6rem' }}>
          <option>GBP</option><option>EUR</option><option>USD</option>
        </select>
        <input placeholder="Lead applicant" value={f.leadApplicant} disabled={busy}
               onChange={(e) => setF({ ...f, leadApplicant: e.target.value })} />
      </div>
      {err && <p className="composer-err" role="alert">{err}</p>}
      <div className="composer-actions">
        <button className="btn-save" disabled={busy || !f.title.trim()} onClick={save}>
          {busy ? 'Creating' : 'Create draft'}
        </button>
      </div>
    </div>
  );
}

function ApplicationCard({ app, contracts, onChanged }: {
  app: Application; contracts: Array<{ id: string; reference: string; title: string }>;
  onChanged: () => void;
}) {
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const days = daysUntil(app.call_closes);
  const settled = CLOSED.includes(app.status);

  async function setStatus(status: AppStatus) {
    setErr(null);
    try {
      const patch: any = { status };
      // Stamp the date the state actually changed, so the record does not depend on someone
      // remembering to fill a field in.
      if (status === 'submitted' && !app.submitted_on) patch.submitted_on = new Date().toISOString().slice(0, 10);
      if ((status === 'awarded' || status === 'rejected') && !app.decision_on) {
        patch.decision_on = new Date().toISOString().slice(0, 10);
      }
      await updateApplication(app.id, patch);
      onChanged();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  async function remove() {
    setErr(null);
    try { await deleteApplication(app.id, app.status); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <article className={`card g-app ${settled ? 'g-settled' : ''}`}>
      <header className="g-app-head">
        <div>
          <h3>{app.title}</h3>
          <div className="g-app-sub">
            {app.funder_name ?? 'No funder recorded'}
            {app.reference && <span className="mono"> · {app.reference}</span>}
            {app.call_url && (
              <> · <a href={app.call_url} target="_blank" rel="noopener noreferrer">call details</a></>
            )}
          </div>
        </div>
        <div className="g-app-right">
          {days != null && !settled && (
            <span className={`pill ${days < 30 ? 'risk' : days < 90 ? 'warn' : 'mute'}`}>
              {days < 0 ? 'closed' : `${days} days`}
            </span>
          )}
          <select value={app.status} onChange={(e) => setStatus(e.target.value as AppStatus)}>
            {APP_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
      </header>

      <div className="g-app-meta">
        {app.requested_amount != null && (
          <span className="pill accent">
            {new Intl.NumberFormat('en-GB', { style: 'currency', currency: app.currency ?? 'GBP',
              maximumFractionDigits: 0 }).format(app.requested_amount)} requested
          </span>
        )}
        {app.awarded_amount != null && (
          <span className="pill ok">
            {new Intl.NumberFormat('en-GB', { style: 'currency', currency: app.currency ?? 'GBP',
              maximumFractionDigits: 0 }).format(app.awarded_amount)} awarded
          </span>
        )}
        {app.submitted_on && <span>submitted {new Date(app.submitted_on).toLocaleDateString('en-GB')}</span>}
        <button className="link-btn" onClick={() => setOpen(!open)}>
          {open ? 'Hide forms' : 'Forms and attachments'}
        </button>
        {app.status === 'drafting' && (
          <button className="att-remove" onClick={remove}>Delete draft</button>
        )}
        {/* An awarded grant is delivered under a contract. Linking them means the money and
            the obligations are one story rather than two modules. */}
        {(app.status === 'awarded' || app.funding_contract_id) && (
          <select value={app.funding_contract_id ?? ''}
                  onChange={(e) => updateApplication(app.id, {
                    funding_contract_id: e.target.value || null } as never).then(onChanged)}>
            <option value="">link a funding contract…</option>
            {contracts.map((k) => (
              <option key={k.id} value={k.id}>{k.reference}: {k.title}</option>
            ))}
          </select>
        )}
      </div>

      {app.feedback && <p className="q-statement">{app.feedback}</p>}
      {err && <p className="composer-err" role="alert">{err}</p>}

      {open && (
        /* draft and submitted are distinct roles on purpose: confusing the version you were
           working on with the one that actually went in is a real problem at audit. */
        <Attachments objectType="grant_application" objectId={app.id} bucket="grants"
                     label="Application forms" roles={['draft', 'submitted', 'evidence', 'attachment']} />
      )}
    </article>
  );
}

function CallsTab({ calls, funders, onChanged }: {
  calls: Call[]; funders: Funder[]; onChanged: () => void;
}) {
  const [adding, setAdding] = useState<'call' | 'funder' | null>(null);
  const [c, setC] = useState({ funderId: '', name: '', url: '', closesOn: '', maxAward: '', ourFit: '' });
  const [fd, setFd] = useState({ name: '', country: 'GBR', url: '' });
  const [err, setErr] = useState<string | null>(null);

  async function saveCall() {
    setErr(null);
    try {
      await createCall({ funderId: c.funderId, name: c.name, url: c.url,
        closesOn: c.closesOn || undefined,
        maxAward: c.maxAward ? Number(c.maxAward) : undefined, ourFit: c.ourFit });
      setC({ funderId: '', name: '', url: '', closesOn: '', maxAward: '', ourFit: '' });
      setAdding(null); onChanged();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }
  async function saveFunder() {
    setErr(null);
    try {
      await createFunder({ name: fd.name, country: fd.country, url: fd.url });
      setFd({ name: '', country: 'GBR', url: '' }); setAdding(null); onChanged();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <>
      <div className="g-call-actions">
        <button className="btn-compose" onClick={() => setAdding(adding === 'funder' ? null : 'funder')}>
          Add funder
        </button>
        <button className="btn-compose" disabled={!funders.length}
                onClick={() => setAdding(adding === 'call' ? null : 'call')}>
          Add call
        </button>
        {!funders.length && <span className="g-hint">Add a funder first.</span>}
      </div>

      {adding === 'funder' && (
        <div className="composer card q-form">
          <div className="composer-row">
            <input placeholder="Funder name" value={fd.name} className="grow"
                   onChange={(e) => setFd({ ...fd, name: e.target.value })} />
            <input placeholder="Country" value={fd.country} style={{ maxWidth: '6rem' }}
                   onChange={(e) => setFd({ ...fd, country: e.target.value })} />
          </div>
          <input placeholder="Website" value={fd.url}
                 onChange={(e) => setFd({ ...fd, url: e.target.value })} />
          {err && <p className="composer-err">{err}</p>}
          <div className="composer-actions">
            <button className="btn-save" disabled={!fd.name.trim()} onClick={saveFunder}>Add funder</button>
          </div>
        </div>
      )}

      {adding === 'call' && (
        <div className="composer card q-form">
          <div className="composer-row">
            <select value={c.funderId} onChange={(e) => setC({ ...c, funderId: e.target.value })}>
              <option value="">Funder…</option>
              {funders.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
            <input placeholder="Call name" className="grow" value={c.name}
                   onChange={(e) => setC({ ...c, name: e.target.value })} />
          </div>
          <div className="composer-row">
            <label className="composer-ack">Closes
              <input type="date" value={c.closesOn}
                     onChange={(e) => setC({ ...c, closesOn: e.target.value })} />
            </label>
            <input type="number" placeholder="Max award" value={c.maxAward}
                   onChange={(e) => setC({ ...c, maxAward: e.target.value })} />
            <input placeholder="Call URL" className="grow" value={c.url}
                   onChange={(e) => setC({ ...c, url: e.target.value })} />
          </div>
          {err && <p className="composer-err">{err}</p>}
          <div className="composer-actions">
            <button className="btn-save" disabled={!c.funderId || !c.name.trim()} onClick={saveCall}>
              Add call
            </button>
          </div>
        </div>
      )}

      {calls.length === 0 && <p className="block-empty">No calls recorded yet.</p>}

      {calls.map((call) => {
        const d = daysUntil(call.closes_on);
        return (
          <article key={call.id} className="card g-call">
            <div className="g-call-when">
              <span className="g-call-d">{d == null ? '—' : d < 0 ? 'closed' : d}</span>
              {d != null && d >= 0 && <span className="g-call-l">days</span>}
            </div>
            <div className="g-call-main">
              <h3>{call.name}</h3>
              <div className="g-app-sub">
                {call.funder_name}
                {call.closes_on && ` · closes ${new Date(call.closes_on).toLocaleDateString('en-GB')}`}
                {call.url && <> · <a href={call.url} target="_blank" rel="noopener noreferrer">details</a></>}
              </div>
            </div>
          </article>
        );
      })}
    </>
  );
}
